import { type NextRequest } from 'next/server'
import { createLogger } from 'shared-utils'

const logger = createLogger('api')

const EXCLUDED_PATHS = ['/api/auth']

type RouteHandler = (request: NextRequest) => Response | Promise<Response>

/**
 * Logs App Router API traffic under the `api` namespace. A `Request`/`Response` body
 * can only be consumed once, so both are read off a `clone()` — and only when
 * the `api` namespace is actually switched on, so the extra buffering costs
 * nothing in normal runs.
 *
 * Non-JSON responses (audio/file streams) are logged by status only; cloning
 * those would buffer the whole stream in memory.
 */
export function routeHandlerWithLogging(handler: RouteHandler): RouteHandler {
  return async (request: NextRequest) => {
    const { pathname } = request.nextUrl

    if (!logger.isEnabled || EXCLUDED_PATHS.some((p) => pathname.startsWith(p))) {
      return handler(request)
    }

    const requestBody = await request
      .clone()
      .text()
      .catch(() => undefined)

    logger.log('[REQ]', request.method, pathname, requestBody)

    const response = await handler(request)

    const responseBody = response.headers.get('content-type')?.includes('application/json')
      ? await response
          .clone()
          .text()
          .catch(() => undefined)
      : '<non-json body>'

    logger.log('[RES]', request.method, pathname, response.status, responseBody)

    return response
  }
}
